import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import type { ComponentPropsWithoutRef } from 'react';
import { renderLatexToHtml } from '../lib/latex';
import './markdown.css';

/*
 * remark-math depose la formule dans <code class="language-math math-inline"> —
 * la forme sur laquelle rehype-katex se branchait. On la rend nous-memes, et
 * rehype-katex ne doit PAS revenir : il embarque son propre KaTeX (0.16.x,
 * resolu separement du 0.17 de l'app), sur lequel l'enregistrement de mhchem
 * cote app ne porte pas. Mesure : avec rehype-katex, \ce{H2SO4} sort en
 * « \ce H2SO4 » dans le chat alors que l'export PDF sort H2SO4 — et l'import
 * de mhchem dans ce fichier n'y change rien, ce qui rend la panne invisible
 * a la relecture.
 */
type MathKind = 'inline' | 'display';

type HastNode = {
  readonly type?: string;
  readonly tagName?: string;
  readonly properties?: { readonly className?: unknown };
  readonly children?: readonly HastNode[];
  readonly value?: string;
};

function mathKind(node: HastNode | undefined): MathKind | null {
  if (node?.type !== 'element' || node.tagName !== 'code') return null;
  const raw = node.properties?.className;
  const classes = Array.isArray(raw) ? raw.map(String) : typeof raw === 'string' ? raw.split(/\s+/) : [];
  if (!classes.includes('language-math')) return null;
  return classes.includes('math-display') ? 'display' : 'inline';
}

function texOf(node: HastNode | undefined): string {
  return (node?.children ?? []).map((child) => (typeof child.value === 'string' ? child.value : '')).join('');
}

interface InlineRef {
  document_id: string;
  page: number;
  title?: string | null;
}

/* ⟦N⟧ (U+27E6 / U+27E7) are used as safe citation placeholders in the AST. */
/* remark-gfm would parse [[N]] as a link-reference node (fragmenting the pattern */
/* across text + linkReference + text nodes), so we pre-process [[N]] → ⟦N⟧ */
/* before passing to ReactMarkdown — these chars are never parsed as markdown. */
/* The model sometimes emits grouped citations: [[1], [4], [10]] — we normalise */
/* those too so each index becomes its own ⟦N⟧ token. */
/* Broad regex: [[...digits/commas/spaces/brackets...]] (no letters → no false positives). */
const CITE_GROUP_RE = /\[\[[\d,\s[\]]+\]\]/g;
const CITE_SAFE_RE = /([⟦]\d+[⟧])/;

/* Remark plugin: transform ⟦N⟧ citation markers into <cite data-n="N"/> nodes. */
/* Walks the mdast tree in reverse-index order so splices don't corrupt sibling indices. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
function remarkInlineCitations(): (tree: any) => void {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  return (tree: any) => {
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    function walk(node: any, parent?: any, idx?: number) {
      if (node.type === 'text' && typeof node.value === 'string') {
        const parts: string[] = node.value.split(CITE_SAFE_RE);
        if (parts.length > 1 && parent && typeof idx === 'number') {
          /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
          const newNodes = parts.flatMap((part): any[] => {
            const m = part.match(/^[⟦](\d+)[⟧]$/);
            if (m) return [{ type: 'inlineCitation', data: { hName: 'cite', hProperties: { 'data-n': m[1] } }, children: [] }];
            return part ? [{ type: 'text', value: part }] : [];
          });
          parent.children.splice(idx, 1, ...newNodes);
          return;
        }
      }
      if (Array.isArray(node.children)) {
        for (let i = node.children.length - 1; i >= 0; i--) {
          walk(node.children[i], node, i);
        }
      }
    }
    walk(tree);
  };
}

/* Markdown renderer for chat bubbles, backed by react-markdown + remark-gfm */
/* (GitHub-flavoured: tables, strikethrough, autolinks…) + remark-math for */
/* LaTeX ($…$ inline, $$…$$ display), rendu par lib/latex.ts — le meme */
/* renderer que l'export PDF (voir le bloc mathKind plus haut). */
/* Styling lives in markdown.css (scoped to .md-body). Links open in a new tab. */
/* Optional refs + onOpenDoc enable [[N]] inline citations rendered as clickable */
/* superscript buttons that open the document drawer at the referenced page. */
export default function Markdown({
  text,
  refs,
  onOpenDoc,
}: {
  text: string;
  refs?: InlineRef[];
  onOpenDoc?: (docId: string, page: number) => void;
}) {
  /* Pre-process [[N]] → ⟦N⟧ and [[1], [4], [10]] → ⟦1⟧⟦4⟧⟦10⟧ before the */
  /* markdown parser sees the text, preventing remark-gfm from interpreting */
  /* [[N]] as link-reference AST nodes. */
  const safeText = text.replace(CITE_GROUP_RE, (m) =>
    (m.match(/\d+/g) ?? []).map((n) => `⟦${n}⟧`).join(''),
  );
  return (
    <div className="md-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath, remarkInlineCitations]}
        components={{
          pre: ({ node, children, ...props }: ComponentPropsWithoutRef<'pre'> & { node?: unknown }) => {
            if (mathKind((node as HastNode | undefined)?.children?.[0]) === 'display') return <>{children}</>;
            return <pre {...props}>{children}</pre>;
          },
          code: ({ node, children, ...props }: ComponentPropsWithoutRef<'code'> & { node?: unknown }) => {
            const kind = mathKind(node as HastNode | undefined);
            if (!kind) return <code {...props}>{children}</code>;
            const html = renderLatexToHtml(texOf(node as HastNode), kind === 'display');
            return kind === 'display'
              ? <div dangerouslySetInnerHTML={{ __html: html }} />
              : <span dangerouslySetInnerHTML={{ __html: html }} />;
          },
          a: ({ node: _node, ...props }: ComponentPropsWithoutRef<'a'> & { node?: unknown }) => (
            <a {...props} target="_blank" rel="noreferrer noopener" />
          ),
          cite: ({ 'data-n': n }: ComponentPropsWithoutRef<'cite'> & { node?: unknown; 'data-n'?: string }) => {
            const idx = parseInt(n ?? '', 10) - 1;
            const ref = refs?.[idx];
            if (!ref || !onOpenDoc) {
              return <sup style={{ fontSize: '0.72em', opacity: 0.75, marginLeft: 1 }}>[{n ?? '?'}]</sup>;
            }
            return (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); onOpenDoc(ref.document_id, ref.page); }}
                title={ref.title ? `${ref.title} — p.${ref.page}` : `p.${ref.page}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  verticalAlign: 'super',
                  fontSize: '0.72em',
                  lineHeight: 1,
                  fontWeight: 700,
                  color: 'var(--naia-blue)',
                  background: 'var(--naia-card-alt)',
                  border: '1px solid var(--naia-line)',
                  borderRadius: '3px',
                  padding: '0 4px',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  marginLeft: 2,
                }}
              >
                {idx + 1}
              </button>
            );
          },
        }}
      >
        {safeText}
      </ReactMarkdown>
    </div>
  );
}
